<script setup lang="ts">
/**
 * 冒険の手記の**額**（#899 / #900）。
 *
 * 中身の絵が誰の手であっても「1 冊の本」に見えるように、**ブランドは絵柄ではなく額装が持つ**
 * （`doc/design/art_direction.md` §6.3）。だから額は 1 種類だけで、エリアごとに変えない。
 *
 * 額は写真AC の木枠を 90° 回して**開口を透過に抜いてある**ので、絵は額の**後ろ**に敷いて
 * 少しだけ潜り込ませる（bleed）。こうすると拡大率の丸めで白い隙間が出ない。
 * 素材の加工は `scripts/gen_memoir_frame.py`（開口の inset を印字するので、素材を
 * 差し替えたら下の CSS に反映すること）。
 *
 * ⚠️ **写真AC の素材は配布経路に置かない**（`doc/domain/asset_licensing.md`）。
 * 額は UI の装飾に留める —— pyxapp に同梱したり素材として配ったりしないこと。
 *
 * 幅は**親が決める**（本文中では float の幅、一覧ではグリッドのセル幅）。この
 * コンポーネントは与えられた幅に対して 900:1215 の比率で立つだけ。
 */
defineProps<{
  /**
   * 額に入れる絵の URL。**解決は呼び出し側**がする —— 中身は場面ごとに出どころが違う
   * （物語ページは `story/Image_*` の挿絵、一覧はエリアのマップ画像）。
   * 空なら中身の無い額になり、`#empty` スロットが出る。
   */
  src?: string | null
  /** 絵の代替テキスト。額そのものは装飾なので alt を持たない。 */
  alt?: string
}>()
</script>

<template>
  <div class="memoir-frame">
    <!-- 開口の中身。絵が無いときは呼び出し側が置くもの（一覧の「？」等）が出る。 -->
    <div class="memoir-frame__opening">
      <img
        v-if="src"
        class="memoir-frame__art"
        :src="src"
        :alt="alt ?? ''"
      >
      <slot
        v-else
        name="empty"
      />

      <!-- 絵があってもなくても額の中に重なる見出し（一覧のエリア名）。 -->
      <div
        v-if="$slots.caption"
        class="memoir-frame__caption"
      >
        <slot name="caption" />
      </div>
    </div>

    <!-- 額。これが高さを決めるので、in-flow のまま置く。 -->
    <img
      class="memoir-frame__wood"
      src="~/assets/images/story/Frame_Wood.webp"
      alt=""
      aria-hidden="true"
    >
  </div>
</template>

<style scoped>
.memoir-frame {
  position: relative;
  width: 100%;
  /* 額の内側（「？」や挿絵）は文字列選択させない。棚を横にドラッグすると
     「？」が次々ハイライトされて、額の中身が「文字」に見えてしまう。 */
  user-select: none;
  -webkit-user-select: none;
}

.memoir-frame__wood {
  display: block;
  width: 100%;
  height: auto;
  /* 絵の上に重ねる（開口が透過しているので、絵は額の内側にだけ見える）。 */
  position: relative;
  z-index: 1;
  pointer-events: none;
}

/* 開口（額の内側）。scripts/gen_memoir_frame.py が測った実値は
   top 4.97% / right 7.00% / bottom 4.89% / left 6.89%。
   そこから 0.4% ほど外に広げて、木枠の下に潜り込ませてある（bleed）。
   **素材を作り直したらこの 4 つを差し替える**（スクリプトが実値を印字する）。 */
.memoir-frame__opening {
  position: absolute;
  top: 4.6%;
  right: 6.6%;
  bottom: 4.5%;
  left: 6.5%;
  z-index: 0;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;
}

.memoir-frame__art {
  width: 100%;
  height: 100%;
  /* 挿絵は 2:3（開口の比率）で作る約束だが、比率が違う絵を入れても破綻しないようにする。 */
  object-fit: cover;
}

/* 絵の上に重ねる見出し。よろず屋の商品カード（`emporium/ProductCard.vue` の
   `.overlay-text`）と同じ「黒文字＋白の縁取り」で、帯は敷かない —— 絵の明暗に
   関係なく読めて、絵を隠さない。 */
.memoir-frame__caption {
  position: absolute;
  /* 上下は開口の中央（よろず屋の商品カードと同じ当て方）。 */
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  /* 左右の余白はほぼ取らない —— 「クリスタルウェーブ島」が折り返さない幅を優先する。 */
  padding: 0 1px;
  text-align: center;
  line-height: 1.25;
  font-weight: bold;
  color: #000;
  text-shadow:
    1px 1px 0 #fff,
    -1px -1px 0 #fff,
    -1px 1px 0 #fff,
    1px -1px 0 #fff,
    0 1px 0 #fff,
    0 -1px 0 #fff,
    -1px 0 0 #fff,
    1px 0 0 #fff;
  word-break: keep-all;
  overflow-wrap: anywhere;
}
</style>
